import styles from "./endPoint.module.scss";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext, AppAction } from "helpers/context";
import {
	Box, Button, IconButton, Table, TableBody, TableCell,
	TableHead, TableRow, Toolbar, TableSortLabel, Tooltip,
	Dialog, DialogTitle, DialogActions, DialogContent, TablePagination
} from "@mui/material";

import SearchInput from "./search-input/search-input";
import StatusDropdown from "./status-dropdown/status-dropdown";
import AddModal from "./add-modal/add-modal";
import DeleteModal from "./delete-modal/delete-modal";

import { getEndPoint } from "api/layer";

const EndPoint = () => {
	const [context, dispatch] = useContext(AppContext);
	const [t] = useTranslation();
	const [showFilters, setShowFilters] = useState(false);
	const [open, setOpen] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [currentInfo, setCurrentInfo] = useState<any>();
	const [searchKeyword, setSearchKeyword] = useState("");
	const [reverseSelect, setReverseSelect] = useState<boolean>(false);
	const [page, setPage] = useState(0);
	const [statusDropdown, setStatusDropdown] = useState<any[]>([]);
	const [tableData, setTableData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [hoveredRow, setHoveredRow] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [sortByField, setSortByField] = useState<"name" | "path" | "http_method" | "is_public" | "extra_param" | "api_type" | "description" | "disabled">("name");
	const [sortOrder, setSortOrder] = useState<{
		name: "asc" | "desc",
		path: "asc" | "desc",
		http_method: "asc" | "desc",
		is_public: "asc" | "desc",
		extra_param: "asc" | "desc",
		api_type: "asc" | "desc",
		description: "asc" | "desc",
		disabled: "asc" | "desc"
	}>({
		name: "asc",
		path: "asc",
		http_method: "asc",
		is_public: "asc",
		extra_param: "asc",
		api_type: "asc",
		description: "desc",
		disabled: "asc",
	});

	const toggleFilters = () => {
		setShowFilters((prev) => !prev);
	};

	const clearFilters = () => {
		setShowFilters(false);
	};

	const handleSearch = (keyword: string, reverseSelect: boolean) => {
		setSearchKeyword(keyword);
		setReverseSelect(reverseSelect)
		setPage(0);
	};

	const handleStatusDropdown = (value: any[]) => {
		setStatusDropdown(value);
		setPage(0);
	};

	const handleDelete = (item: any) => {
		setCurrentInfo(item);
		setOpenDeleteModal(true);
	};

	const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSave = () => {
		setOpen(false);
		initData();
	};

	const initData = async () => {
		setIsLoading(true);

		const param: any = {
			keyword: searchKeyword,
		};

		const pageParam: any = {
			pageNo: page + 1,
			pageSize: rowsPerPage,
			reverseSelect: reverseSelect,
			sortOrderAsc: sortOrder[sortByField] === "asc",
			sortByField: sortByField
		};

		if (statusDropdown && statusDropdown.length > 0) {
			param.endpointTypes = statusDropdown;
		}

		getEndPoint(param, pageParam).then((res: any) => {
			if (res.code !== 200 || !res.data || !res.data.rows) {
				return;
			}
			setTableData(res.data.rows);
			setTotalCount(res.data.totalCount * 1 || 0);
			setIsLoading(false);
		});
	};


	useEffect(() => {
		initData();
	}, [page, rowsPerPage, searchKeyword, reverseSelect, statusDropdown, sortByField, sortOrder]);

	return (<div className={styles.container}>
		<div className={styles.title}>{t("userSystem.endPoint")}</div>
		<Toolbar style={{ justifyContent: "space-between", borderBottom: "1px solid #ccc", marginBottom: "5px", padding: "0" }}>
			<Button
				variant="contained"
				color="success"
				size="small"
				endIcon={<span className="material-icons">add</span>}
				onClick={() => {
					setOpen(true);
					setCurrentInfo(null);
				}}
			>
				{t("userSystem.addEndPoint")}
			</Button>

			<IconButton
				color="success"
				onClick={toggleFilters}
				style={{
					color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
					background: showFilters ? "#c1e5ca" : "",
				}}
			>
				<span className="material-icons">filter_list</span>
			</IconButton>
		</Toolbar>

		{showFilters && (
			<Box style={{ display: "flex", alignItems: "center", padding: "8px", backgroundColor: "#f5f5f5", justifyContent: "space-between" }}>
				<Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
					<SearchInput onSearch={handleSearch} />
					<StatusDropdown
						selectedStatus={statusDropdown}
						onSave={(value) => handleStatusDropdown(value)}
					/>
				</Box>
				<IconButton
					onClick={clearFilters}
					aria-label="clear"
					color="default"
				>
					<span className="material-icons">clear</span>
				</IconButton>
			</Box>
		)}

		<div style={{ flex: 1, overflow: "auto" }}>
			<Table
				stickyHeader
				aria-label="sticky table"
				size="small"
				style={{
					tableLayout: "fixed",
					width: "100%",
					minWidth: "100%"
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell>
							<TableSortLabel
								active={sortByField === "name"}
								onClick={() => {
									setSortByField("name")
									let sort: any = {
										...sortOrder,
										name: sortOrder.name === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.name}
							>
								{t("userSystem.endPointName")}
							</TableSortLabel>
						</TableCell>
						<TableCell>
							<TableSortLabel
								active={sortByField === "path"}
								onClick={() => {
									setSortByField("path")
									let sort: any = {
										...sortOrder,
										path: sortOrder.path === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.path}
							>
								{t("userSystem.path")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 100
						}}>
							<TableSortLabel
								active={sortByField === "http_method"}
								onClick={() => {
									setSortByField("http_method")
									let sort: any = {
										...sortOrder,
										http_method: sortOrder.http_method === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.http_method}
							>
								{t("userSystem.httpMethod")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 100
						}}>
							<TableSortLabel
								active={sortByField === "extra_param"}
								onClick={() => {
									setSortByField("extra_param")
									let sort: any = {
										...sortOrder,
										extra_param: sortOrder.extra_param === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.extra_param}
							>
								{t("userSystem.extraParam")}
							</TableSortLabel>
						</TableCell>
						<TableCell
							style={{
								width: 100
							}}
						>
							<TableSortLabel
								active={sortByField === "api_type"}
								onClick={() => {
									setSortByField("api_type")
									let sort: any = {
										...sortOrder,
										api_type: sortOrder.api_type === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.api_type}
							>
								{t("userSystem.endPointType")}
							</TableSortLabel>
						</TableCell>
						<TableCell
							style={{
								width: 125
							}}
						>
							<TableSortLabel
								active={sortByField === "description"}
								onClick={() => {
									setSortByField("description")
									let sort: any = {
										...sortOrder,
										description: sortOrder.description === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.description}
							>
								{t("userSystem.description")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 100
						}}>
							<TableSortLabel
								active={sortByField === "is_public"}
								onClick={() => {
									setSortByField("is_public")
									let sort: any = {
										...sortOrder,
										is_public: sortOrder.is_public === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.is_public}
							>
								{t("userSystem.isPublic")}
							</TableSortLabel>
						</TableCell>
						<TableCell
							style={{
								width: 100,
							}}
						>
							<TableSortLabel
								active={sortByField === "disabled"}
								onClick={() => {
									setSortByField("disabled")
									let sort: any = {
										...sortOrder,
										disabled: sortOrder.disabled === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.disabled}
							>
								{t("userSystem.editStatus")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{ padding: 0, width: 0 }}></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{isLoading ? (
						<TableRow>
							<TableCell colSpan={8}>
								<div className={styles.loading}>
									<div className="loading loading-spinner loading-lg"></div>
									<div>{t("layers.loading")}</div>
								</div>
							</TableCell>
						</TableRow>
					) : (
						tableData.map((item: any) => (
							<TableRow
								key={item.id}
								hover
								onMouseEnter={() => setHoveredRow(item.id)}
								onMouseLeave={() => setHoveredRow(null)}
								className={styles.row}
							>
								<TableCell>{item.name}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.path}</TableCell>
								<TableCell>{item.httpMethod}</TableCell>
								<TableCell>{item.extraParam}</TableCell>
								<TableCell>{item.apiType}</TableCell>
								<TableCell>{item.description}</TableCell>
								<TableCell>{item.isPublic ? t("userSystem.yes") : t("userSystem.no")}</TableCell>
								<TableCell>{item.disabled ? t("userSystem.yes") : t("userSystem.no")}</TableCell>
								<TableCell style={{ padding: 0, position: "relative" }}>
									{hoveredRow === item.id && (
										<div
											className={`${styles.icons}`}
										>
											<Tooltip
												title={t("tooltip.edit")}
											>
												<IconButton
													onClick={() => {
														setOpen(true);
														setCurrentInfo(item);
													}}
												>
													<span className="material-icons">edit</span>
												</IconButton>
											</Tooltip>

											<Tooltip
												title={t("tooltip.delete")}
											>
												<IconButton onClick={() => handleDelete(item)}>
													<span className="material-icons">delete</span>
												</IconButton>
											</Tooltip>
										</div>
									)}
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
		<div style={{ marginLeft: "auto" }}>
			<TablePagination
				component="div"
				count={totalCount}
				rowsPerPageOptions={[3, 10, 15, 25]}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				labelRowsPerPage={t("userSystem.rowsPerPage")}
				slotProps={{
					select: {
						native: true,
					},
				}}
			/>
		</div>

		{open && (
			<AddModal
				open={open}
				onClose={() => setOpen(false)}
				onSave={handleSave}
				data={currentInfo}
			/>
		)}

		{openDeleteModal && (
			<DeleteModal
				data={currentInfo}
				initData={initData}
				onClose={() => setOpenDeleteModal(false)}
			/>
		)}
	</div>)
};

export default EndPoint;
